/**
 * TypeScript interface for AccountModel
 *
 * This interface represents the structure of the AccountModel from the backend.
 * It defines accounts used for financial transactions in the accounting system.
 */

import { ChartOfAccountModel } from './chart-of-account.type';
import { ApiResponse } from '../api.type';

/**
 * Balance type constants
 */
export const DEBIT = 'debit';
export const CREDIT = 'credit';

/**
 * Balance type options
 */
export type BalanceType = typeof DEBIT | typeof CREDIT;

/**
 * Account role options
 */
export enum AccountRole {
  IN_OTHER = 'in_other',
  ASSET = 'asset',
  LIABILITY = 'liability',
  EQUITY = 'equity',
  INCOME = 'income',
  EXPENSE = 'expense',
  COGS = 'cogs',
  CURRENT_ASSET = 'current_asset',
  CURRENT_LIABILITY = 'current_liability',
  EQUITY_PERMANENT = 'equity_permanent',
  EQUITY_TEMPORARY = 'equity_temporary',
  INCOME_OPERATIONAL = 'income_operational',
  INCOME_OTHER = 'income_other',
  EXPENSE_OPERATIONAL = 'expense_operational',
  EXPENSE_OTHER = 'expense_other'
}

/**
 * Interface for AccountConstraint
 */
export interface AccountConstraint {
  /**
   * Constraint ID
   */
  id?: number;

  /**
   * Unique identifier (UUID)
   */
  uuid?: string;

  /**
   * Reference to the account (UUID)
   */
  account: string;

  /**
   * Constraint code
   */
  constraint_code: string;

  /**
   * Constraint line
   */
  constraint_line: number;

  /**
   * Constraint name
   */
  constraint_name: string;

  /**
   * Whether the constraint is mandatory
   */
  is_mandatory: boolean;

  /**
   * Timestamp of creation
   */
  created?: string;

  /**
   * Timestamp of last update
   */
  updated?: string;
}

export interface AccountConstraintInput {
  uuid?: string;
  account?: string;
  constraint_code: string;
  constraint_line: number;
  constraint_name: string;
  is_mandatory: boolean;
}

/**
 * Interface for AccountModel
 */
export interface AccountModel {
  tk?: string;
  ten_tk?: string;
  tk_me?: string | null;
  tk_sc?: boolean;
  ct_yn?: boolean;
  ma_nt?: string | null;
  tk_cn?: string | null;
  ten_loai_tk?: string | null;
  loai_so_du?: string | null;
  loai_cl_no?: string | null;
  loai_cl_co?: string | null;
  status?: boolean;
  /**
   * Unique identifier (UUID)
   */
  uuid: string;

  /**
   * Account code
   */
  code: string;

  /**
   * Account name
   */
  name: string;

  /**
   * Account role
   */
  role: AccountRole;

  /**
   * Whether this account is the default for its role
   */
  role_default?: boolean | null;

  /**
   * Account balance type (debit or credit)
   */
  balance_type: BalanceType;

  /**
   * Whether the account is locked
   */
  locked: boolean;

  /**
   * Whether the account is active
   */
  active: boolean;

  /**
   * Alternative account name 2
   */
  account_name_2?: string | null;

  /**
   * Alternative account name 3
   */
  account_name_3?: string | null;

  /**
   * Parent account code
   */
  parent_account_code?: string | null;

  /**
   * Account group 1
   */
  account_group_1?: string | null;

  /**
   * Short name 1
   */
  short_name_1?: string | null;

  /**
   * Short name 2
   */
  short_name_2?: string | null;

  /**
   * Whether this is a parent account
   */
  is_parent_account?: boolean;

  /**
   * Currency code
   */
  currency_code?: string;

  /**
   * AR/AP tracking type
   */
  ar_ap_tracking_type?: number;

  /**
   * Account type code
   */
  account_type_code?: string | null;

  /**
   * FX revaluation debit type
   */
  fx_reval_debit_type?: number;

  /**
   * FX revaluation credit type
   */
  fx_reval_credit_type?: number;

  /**
   * Notes
   */
  notes?: string | null;

  /**
   * Account constraints
   */
  constraints?: AccountConstraint[];

  /**
   * Reference to the chart of accounts model (UUID)
   */
  coa_model?: string;

  /**
   * Chart of accounts data
   */
  coa_model_data?: ChartOfAccountModel;

  /**
   * Timestamp of creation
   */
  created: string;

  /**
   * Timestamp of last update
   */
  updated: string;

  /**
   * Whether this is a general ledger account
   */
  tk_so_cai?: boolean;

  /**
   * Whether this is a detail account
   */
  tk_chi_tiet?: boolean;

  /**
   * Account level/depth in the hierarchy
   */
  bac_tk?: number;

  /**
   * Account depth in the hierarchy
   */
  account_depth?: number;

  /**
   * Parent account data
   */
  parent_account_code_data?: AccountModel;

  /**
   * Path in the account hierarchy
   */
  path?: string;

  /**
   * Number of child accounts
   */
  numchild?: number;

  /**
   * Depth in the account hierarchy
   */
  depth?: number;

  /**
   * Role on the balance sheet
   */
  role_bs?: string;
}

export interface AccountFilterRequest {
  tk?: string | string[];
  ten_tk?: string | string[];
  tk_me?: string | string[];
  dynamic_filters?: Record<string, unknown>;
  sort?: Array<{ field: string; direction?: 'asc' | 'desc' | null }>;
  sum?: string[];
}

/**
 * Interface for creating or updating an AccountModel
 */
export interface AccountModelInput {
  /**
   * Account code
   */
  code: string;

  /**
   * Account name
   */
  name: string;

  /**
   * Account role
   */
  role: AccountRole;

  /**
   * Whether this account is the default for its role
   */
  role_default?: boolean | null;

  /**
   * Account balance type (debit or credit)
   */
  balance_type: BalanceType;

  /**
   * Whether the account is active
   */
  active?: boolean;

  /**
   * Alternative account name 2
   */
  account_name_2?: string | null;

  /**
   * Alternative account name 3
   */
  account_name_3?: string | null;

  /**
   * Parent account code
   */
  parent_account_code?: string | null;

  /**
   * Account group 1
   */
  account_group_1?: string | null;

  /**
   * Short name 1
   */
  short_name_1?: string | null;

  /**
   * Short name 2
   */
  short_name_2?: string | null;

  /**
   * Whether this is a parent account
   */
  is_parent_account?: boolean;

  /**
   * Currency code
   */
  currency_code?: string;

  /**
   * AR/AP tracking type
   */
  ar_ap_tracking_type?: number;

  /**
   * Account type code
   */
  account_type_code?: string | null;

  /**
   * FX revaluation debit type
   */
  fx_reval_debit_type?: number;

  /**
   * FX revaluation credit type
   */
  fx_reval_credit_type?: number;

  /**
   * Notes
   */
  notes?: string | null;

  /**
   * Account constraints
   */
  constraints?: AccountConstraintInput[];

  /**
   * Reference to the chart of accounts model (UUID)
   */
  coa_model?: string;

  /**
   * Whether this is a general ledger account
   */
  tk_so_cai?: boolean;

  /**
   * Whether this is a detail account
   */
  tk_chi_tiet?: boolean;

  /**
   * Account level/depth in the hierarchy
   */
  bac_tk?: number;
}

/**
 * Type for AccountModel API response
 */
export type AccountModelResponse = ApiResponse<AccountModel>;

/**
 * Type for AccountModel list API response
 */
export type AccountModelListResponse = ApiResponse<AccountModel[]>;

export type TaiKhoan = AccountModel;
